import type { ValidatedExcelTransaction } from 'store/organisation/models';
import { EDITABLE_ROW_KEYS } from '../transactions-tab/constants';
import type { InterpretationRuleDraft, RuleAction, RuleCondition } from './types';

function newRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  return String(a ?? '').trim() === String(b ?? '').trim();
}

export interface RuleEditSuggestion {
  conditionField: string;
  conditionValue: string;
  actionField: string;
  actionValue: string | number | null;
}

export function suggestInterpretationRulesFromEditDiff(
  before: ValidatedExcelTransaction,
  after: ValidatedExcelTransaction
): RuleEditSuggestion | null {
  const changedKeys = EDITABLE_ROW_KEYS.filter((key) => {
    const b = (before as Record<string, unknown>)[key];
    const a = (after as Record<string, unknown>)[key];
    return !valuesEqual(b, a);
  });

  if (changedKeys.length === 0) return null;

  const actionKey = changedKeys.find(
    (k) =>
      k === 'accountCode' ||
      k === 'taxName' ||
      k === 'contactName' ||
      k === 'itemId'
  ) ?? changedKeys[0];

  const anchorKey =
    changedKeys.find(
      (k) => k === 'contactName' || k === 'description' || k === 'itemId'
    ) ??
    (actionKey === 'accountCode' ? 'contactName' : 'description');

  const anchorBefore = (before as Record<string, unknown>)[anchorKey];
  const actionAfter = (after as Record<string, unknown>)[actionKey];

  if (valuesEqual(anchorBefore, (after as Record<string, unknown>)[anchorKey])) {
    if (!anchorBefore || String(anchorBefore).trim() === '') return null;
  }

  const anchorValue = String(
    (after as Record<string, unknown>)[anchorKey] ??
      anchorBefore ??
      ''
  ).trim();
  if (!anchorValue) return null;

  return {
    conditionField: anchorKey,
    conditionValue: anchorValue,
    actionField: actionKey,
    actionValue:
      actionAfter == null ? null : (actionAfter as string | number),
  };
}

export function buildDraftFromSuggestion(
  suggestion: RuleEditSuggestion,
  enabled = true
): Omit<InterpretationRuleDraft, 'name'> & { name?: string } {
  const conditions: RuleCondition[] = [
    {
      id: newRowId(),
      field: suggestion.conditionField as RuleCondition['field'],
      operator: 'INCLUDES',
      value: suggestion.conditionValue,
    },
  ];
  const actions: RuleAction[] = [
    {
      id: newRowId(),
      field: suggestion.actionField as RuleAction['field'],
      value: suggestion.actionValue,
    },
  ];
  return {
    enabled,
    conditions,
    actions,
  };
}
